// =============================================================================
// useStoreData — store-scoped live subscriptions used by the v3 pages.
// -----------------------------------------------------------------------------
// Recon reads flat, top-level collections keyed by tenantId (+ storeId).
// The v3 pages are per-rooftop, so every hook here narrows to profile.storeId
// when the claim has one, and falls back to the whole tenant when it doesn't
// (group admins browsing without a store pinned).
//
//   useStoreVehicles()  → /vehicles       (active units on the board)
//   useStoreActivity()  → /activity       (append-only feed, newest first)
//   useLotUnits()       → /batteryUnits   (Battery Care, baked into Recon as "Lot")
//   useLoads()          → /shippingLoads  (Shipping, baked into Recon as "Loads")
//   useConnectUnread()  → connectRooms + connectReads → unread count for the dock
//
// All return { data, loading, error }. `data` is always an array.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db } from "./firebase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { toMs } from "./useTenantData.js";
import { isActiveVehicle } from "./useStoreFlow.js";
import { watchRooms, watchReads } from "../connect/db.js";

function useScoped(build, deps, { sort } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const q = build();
    if (!q) { setLoading(false); return undefined; }
    setLoading(true);
    const unsub = onSnapshot(q,
      (snap) => {
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (sort) rows = rows.sort(sort);
        setData(rows); setLoading(false); setError(null);
      },
      (err) => { console.warn("[useStoreData] subscribe failed:", err?.message); setError(err); setLoading(false); }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

export function useStoreVehicles({ includeInactive = false } = {}) {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const storeId = profile?.storeId || null;
  const res = useScoped(() => {
    if (!tenantId) return null;
    const parts = [collection(db, "vehicles"), where("tenantId", "==", tenantId)];
    if (storeId) parts.push(where("storeId", "==", storeId));
    return query(...parts);
  }, [tenantId, storeId]);
  const data = useMemo(() => includeInactive ? res.data : res.data.filter(isActiveVehicle), [res.data, includeInactive]);
  return { ...res, data, tenantId, storeId };
}

export function useStoreActivity(n = 40) {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const storeId = profile?.storeId || null;
  return useScoped(() => {
    if (!tenantId) return null;
    const parts = [collection(db, "activity"), where("tenantId", "==", tenantId)];
    if (storeId) parts.push(where("storeId", "==", storeId));
    parts.push(orderBy("at", "desc"), limit(n));
    return query(...parts);
  }, [tenantId, storeId, n]);
}

export function useLotUnits() {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const storeId = profile?.storeId || null;
  const res = useScoped(() => {
    if (!tenantId) return null;
    const parts = [collection(db, "batteryUnits"), where("tenantId", "==", tenantId)];
    if (storeId) parts.push(where("storeId", "==", storeId));
    return query(...parts);
  }, [tenantId, storeId]);
  const data = useMemo(() => res.data.filter((u) => !u.sold && !u.archivedAt), [res.data]);
  return { ...res, data, tenantId, storeId };
}

export function useLoads() {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const storeId = profile?.storeId || null;
  return useScoped(() => {
    if (!tenantId) return null;
    const parts = [collection(db, "shippingLoads"), where("tenantId", "==", tenantId)];
    if (storeId) parts.push(where("storeId", "==", storeId));
    return query(...parts);
  }, [tenantId, storeId], { sort: (a, b) => (toMs(b.updatedAt || b.createdAt) || 0) - (toMs(a.updatedAt || a.createdAt) || 0) });
}

export function useConnectUnread() {
  const { user, profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const uid = user?.uid || null;
  const [rooms, setRooms] = useState([]);
  const [reads, setReads] = useState({});
  useEffect(() => {
    if (!tenantId || !uid) return undefined;
    let u1 = () => {}, u2 = () => {};
    try { u1 = watchRooms(tenantId, uid, setRooms); u2 = watchReads(uid, setReads); } catch (e) { console.warn("[useConnectUnread]", e?.message); }
    return () => { u1(); u2(); };
  }, [tenantId, uid]);
  return useMemo(() => rooms.reduce((n, r) => {
    if (r.lastMessage?.uid === uid) return n; // your own last message never counts
    return n + Math.max(0, (r.msgCount || 0) - (reads[r.id] || 0));
  }, 0), [rooms, reads, uid]);
}

// ---------- tiny formatters shared by the pages ----------
export const fmt$ = (n) => (n == null || Number.isNaN(Number(n))) ? "—" : Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
export const clock = (ts) => { const ms = toMs(ts); if (!ms) return "—"; const d = new Date(ms); const today = new Date().toDateString() === d.toDateString(); return today ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString("en-US", { weekday: "short" }); };
export const ymm = (v) => [v?.year, v?.make, v?.model].filter(Boolean).join(" ") || "Unit";
export const stockOf = (v) => v?.stock || v?.stockNumber || null;
export const initials = (name = "") => { const p = String(name).split(/[ .]+/).filter(Boolean); if (!p.length) return "—"; return (p[0][0] + (p[1]?.[0] || "")).toUpperCase(); };
export const startOfDay = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };
export const startOfMonth = () => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime(); };
export const actorOf = (a) => { const x = a?.actor; if (!x) return a?.actorName || a?.actorEmail || null; if (typeof x === "string") return x.includes("@") ? x.split("@")[0] : x; return x.name || x.email || null; };
export const toStageOf = (a) => a?.toStage || String(a?.summary || "").split("→").pop().replace(/\(override\)/, "").trim() || null;
